/// <amd-module name="Repos/CommitsList/Components/CommitsHistoryView" />
import * as React from "react";
import { CommitsSearchFilterData, GitCommitSearchResults, ICommitsDataProviderService } from "Repos/CommitsList/CommitsDataProviderService";
import {
    CommitsFilterBar,
    CommitsFilterKeys,
    getCommitsFilterBarStateFromFilterState,
    getInitialCommitsFilterBarState,
    ICommitsFilterBarState
} from "Repos/CommitsList/Components/CommitsFilterBar";
import { CommitsTable } from "Repos/CommitsList/Components/CommitsTable";
import * as Resources from "Repos/CommitsList/Resources";
import { GitHistoryMode, GitStatus, GitVersionOptions, GitVersionType, VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { ICommit, ICommitsHistoryViewProps, ICommitTagRef } from "Repos/Common/Contracts/History";
import { branchNameToRef, getShortCommitId } from "Repos/Common/Util/Ref";
import { getVersionString } from "Repos/Common/Util/Version";
import { getGitRepository, IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { getTFSProject } from "Tfs/Platform/Page";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import * as String from "VSS/Core/Util/String";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Observer } from "VSSUI/Observer";
import { Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { ZeroData } from "VSSUI/ZeroData";
import { HistoryGraph } from "./HistoryGraph";
import { IHistoryGraph } from "./HistoryGraphCell.Props";

interface ICommitsHistoryViewState {
    hasMore: boolean;
}

interface ICommitsSection {
    commits: ObservableArray<ICommit | ObservableValue<ICommit | undefined>>;
    lastCommitIdBeforeRename: ObservableValue<string | undefined>;
    loading?: boolean;
    fileName: ObservableValue<string | undefined>;
    prevFileName: ObservableValue<string | undefined>;
    show: ObservableValue<boolean | undefined>;
}

export class CommitsHistoryView extends VssComponent<ICommitsHistoryViewProps, ICommitsHistoryViewState> {
    private areDetailsLoaded = new ObservableValue(false);
    private commitSections = new ObservableArray<ICommitsSection>([]);
    private filter = new Filter();
    private filterBarState: ICommitsFilterBarState;
    private firstLoaded = new ObservableValue<boolean>(false);
    private historyGraph: IHistoryGraph | undefined;
    private loadHistoryOnMount = false;
    private loadingMore = false;
    private noResultsUrl: string;
    private onLoadedCalled = false;

    constructor(props: ICommitsHistoryViewProps, context: IVssComponentContext) {
        super(props, context);

        const vmDataProviderService = context.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const userPreferences = vmDataProviderService.getUserPreferences();

        const defaultFilterState: IFilterState = {};
        defaultFilterState[CommitsFilterKeys.HistoryType] = { value: [userPreferences.historyMode || GitHistoryMode.SimplifiedHistory] };
        this.filter.setDefaultState(defaultFilterState);

        const initialFilterState = props.shouldUpdateUrl
            ? getInitialCommitsFilterBarState(context.pageContext, defaultFilterState)
            : defaultFilterState;

        this.filter.setState(initialFilterState, true);

        this.filterBarState = getCommitsFilterBarStateFromFilterState(initialFilterState);

        this.commitSections.value = [
            {
                commits: new ObservableArray([]),
                lastCommitIdBeforeRename: new ObservableValue<string | undefined>(undefined),
                fileName: new ObservableValue<string | undefined>(undefined),
                prevFileName: new ObservableValue<string | undefined>(undefined),
                show: new ObservableValue<boolean>(true)
            }
        ];

        let hasMore = false;
        const commitsDataService = context.pageContext.getService<ICommitsDataProviderService>("ICommitsDataProviderService");
        const initialCommitData = commitsDataService.getInitialCommits();
        if (initialCommitData) {
            this.handleCommitsFetchResult(initialCommitData, this.commitSections.value[0]);
            hasMore = !!initialCommitData.hasMore;
        } else {
            this.loadHistoryOnMount = true;
        }

        this.noResultsUrl = getAssetLocation(context.pageContext, "ms.vss-code-web/commits-hub-content/Content/no-results.png");

        this.state = { hasMore };
    }

    public render() {
        return (
            <>
                <ConditionalChildren renderChildren={this.props.filterVisible}>
                    <CommitsFilterBar
                        className={this.props.filterClassName}
                        commitsFilter={this.filter}
                        onFilterChanged={this.performFilter}
                        shouldUpdateUrl={this.props.shouldUpdateUrl}
                    />
                </ConditionalChildren>
                <Observer commitSections={this.commitSections} showGraph={this.props.showGraph}>
                    {(sectionProps: { commitSections: ICommitsSection[]; showGraph: boolean | undefined }) => {
                        return sectionProps.commitSections.map((section, index) => {
                            return (
                                <Observer
                                    commits={section.commits}
                                    key={index}
                                    lastCommitIdBeforeRename={section.lastCommitIdBeforeRename}
                                    fileName={section.fileName}
                                    show={section.show}
                                >
                                    {(commitProps: {
                                        commits: Array<ICommit | ObservableValue<ICommit | undefined>>;
                                        fileName: string | undefined;
                                        lastCommitIdBeforeRename: string | undefined;
                                        show: boolean | undefined;
                                    }) => {
                                        if (index === 0 && commitProps.commits.length === 0 && this.firstLoaded.value) {
                                            return (
                                                <Card className="flex-center" contentProps={{ contentPadding: false }}>
                                                    <ZeroData
                                                        imagePath={this.noResultsUrl}
                                                        imageAltText={"Image"}
                                                        primaryText={Resources.NoResultsPrimaryText}
                                                        secondaryText={Resources.NoResultsSecondaryText}
                                                    />
                                                </Card>
                                            );
                                        }

                                        // Ensure a deep copy. Just doing props.commits.push is triggering the observer to rerender.
                                        const commitsWithLoading = commitProps.commits.slice();
                                        if (this.state.hasMore) {
                                            commitsWithLoading.push(new ObservableValue(undefined));
                                        }
                                        const loadMore = () => {
                                            this.loadMoreData(section);
                                        };

                                        const expandButton = (
                                            event: React.MouseEvent<HTMLElement, MouseEvent> | React.KeyboardEvent<HTMLElement>
                                        ) => {
                                            this.toggleSection(section, this.commitSections.value[index - 1], index);
                                            event.preventDefault();
                                        };

                                        const labelForShowMore = !commitProps.fileName
                                            ? commitProps.show
                                                ? Resources.HideRenameHistory
                                                : Resources.ShowRenameHistory
                                            : commitProps.show
                                            ? String.format(Resources.HideRenameHistoryFor, commitProps.fileName)
                                            : String.format(Resources.ShowRenameHistoryFor, commitProps.fileName);

                                        return (
                                            <div className="flex-column flex-noshrink" key={`commits-key-index-${index}`}>
                                                {index !== 0 && commitProps.lastCommitIdBeforeRename && (
                                                    <Button text={`${labelForShowMore}`} onClick={expandButton} subtle={true} />
                                                )}
                                                <Card className="bolt-table-card" contentProps={{ contentPadding: false }}>
                                                    {commitProps.show && (
                                                        <CommitsTable
                                                            commits={commitsWithLoading}
                                                            dataLoaded={this.firstLoaded}
                                                            hideGraphDrawing={index !== 0}
                                                            historyGraph={this.historyGraph}
                                                            isGraphReady={this.areDetailsLoaded}
                                                            loadMore={this.state.hasMore ? loadMore : undefined}
                                                            onActivate={this.props.onActivate}
                                                            showChangeType={this.props.isSingleFileHistory}
                                                            showCommitDetails={this.props.showCommitDetails ?? true}
                                                            showGraph={
                                                                sectionProps.showGraph && !(this.filterBarState && this.filterBarState.committedBy)
                                                            }
                                                            showHeader={this.props.showHeader !== false && index === 0}
                                                        />
                                                    )}
                                                </Card>
                                            </div>
                                        );
                                    }}
                                </Observer>
                            );
                        });
                    }}
                </Observer>
            </>
        );
    }

    public componentDidMount() {
        const commitsFilterState = getCommitsFilterBarStateFromFilterState(this.filter.getState());
        if (this.loadHistoryOnMount) {
            this.performFilter(commitsFilterState);
        }

        this.triggerLoaded();
    }

    public componentDidUpdate(prevProps: ICommitsHistoryViewProps) {
        if (this.props.filePath !== prevProps.filePath) {
            const commitsFilterState = getCommitsFilterBarStateFromFilterState(this.filter.getState());
            this.performFilter(commitsFilterState);
        }

        this.triggerLoaded();
    }

    private triggerLoaded() {
        if (this.props.onLoaded && !this.onLoadedCalled && this.firstLoaded.value) {
            this.props.onLoaded();
            this.onLoadedCalled = true;
        }
    }

    private toggleSection = (currentSection: ICommitsSection, prevSection: ICommitsSection, index: number) => {
        if (currentSection.show.value !== undefined) {
            currentSection.show.value = !currentSection.show.value;
        } else {
            currentSection.show.value = true;
            this.loadData(currentSection, this.filterBarState, undefined, prevSection);
        }
    };

    private performFilter = (filterbarState: ICommitsFilterBarState) => {
        this.filterBarState = filterbarState;

        // Reset the previous history state
        const section: ICommitsSection = {
            commits: this.getLoadingRows(),
            lastCommitIdBeforeRename: new ObservableValue<string | undefined>(undefined),
            loading: true,
            fileName: new ObservableValue<string | undefined>(undefined),
            prevFileName: new ObservableValue<string | undefined>(undefined),
            show: new ObservableValue<boolean>(true)
        };
        this.commitSections.value = [section];

        this.loadData(section, filterbarState);

        if (filterbarState.historyType != undefined) {
            const vmDataProviderService = this.context.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
            const userPreferences = vmDataProviderService.getUserPreferences();
            if (userPreferences.historyMode !== filterbarState.historyType) {
                userPreferences.historyMode = filterbarState.historyType;
                vmDataProviderService.setUserPreferences(userPreferences);
            }
        }
    };

    private loadMoreData = (section: ICommitsSection): void => {
        if (!this.loadingMore) {
            this.loadingMore = true;
            this.loadData(section, this.filterBarState, section.commits.length);
        }
    };

    private loadData(section: ICommitsSection, filterbarState?: ICommitsFilterBarState, skip?: number, previousSection?: ICommitsSection): void {
        const { compareVersion, filePath, version } = this.props;

        const searchCriteria: CommitsSearchFilterData = {
            committerId: undefined,
            committerName: undefined,
            compareVersion: compareVersion ? getVersionString(compareVersion) : undefined,
            historyMode: undefined,
            filePath: section.prevFileName.value || filePath,
            gitRenameVersion: undefined,
            itemVersion: getVersionString(version),
            fromDate: undefined,
            toDate: undefined,
            skip: skip
        };

        if (section.lastCommitIdBeforeRename.value) {
            searchCriteria.gitRenameVersion = {
                version: section.lastCommitIdBeforeRename.value || "",
                versionType: GitVersionType.Commit,
                versionOptions: GitVersionOptions.PreviousChange
            };
        }

        if (filterbarState) {
            searchCriteria.fromDate = filterbarState.dateFrom;
            searchCriteria.toDate = filterbarState.dateTo;
            searchCriteria.historyMode = filterbarState.historyType;

            if (filterbarState.committedBy) {
                searchCriteria.committerId = filterbarState.committedBy.displayName;
            }
        }

        if (!skip || skip === 0) {
            this.firstLoaded.value = false;
            this.onLoadedCalled = false;
        }

        const commitsDataService = this.context.pageContext.getService<ICommitsDataProviderService>("ICommitsDataProviderService");
        commitsDataService.fetchCommits(searchCriteria, this.props.version).then(commitResults => {
            this.firstLoaded.value = true;
            if (commitResults) {
                this.handleCommitsFetchResult(commitResults, section, skip, !!previousSection);
                this.setState({ hasMore: commitResults.hasMore });
            } else {
                section.commits.value = [];
            }
        });
    }

    private handleCommitsFetchResult(commitResults: GitCommitSearchResults, section: ICommitsSection, skip?: number, noGraph?: boolean) {
        const commitsDataService = this.context.pageContext.getService<ICommitsDataProviderService>("ICommitsDataProviderService");
        this.firstLoaded.value = true;

        if (commitResults.commits.length > 0) {
            let lastCommitIdBeforeRename: string | undefined = undefined;
            const firstCommit = commitResults.commits[0];
            const lastCommit = commitResults.commits[commitResults.commits.length - 1];

            if (lastCommit.changes) {
                for (let index = 0; index < lastCommit.changes.length; index++) {
                    const change = lastCommit.changes[index];
                    if ((change.changeType & VersionControlChangeType.Rename) !== 0) {
                        lastCommitIdBeforeRename = commitResults.commits[commitResults.commits.length - 1].commitId;
                        break;
                    }
                }
            }

            const fileName = firstCommit.changes && firstCommit.changes[0] && firstCommit.changes[0].sourceServerItem;
            section.fileName.value = fileName;

            if (lastCommitIdBeforeRename) {
                const commitsSection: ICommitsSection = {
                    commits: this.getLoadingRows(),
                    lastCommitIdBeforeRename: new ObservableValue<string | undefined>(lastCommitIdBeforeRename),
                    fileName: new ObservableValue<string | undefined>(undefined),
                    prevFileName: new ObservableValue<string | undefined>(fileName),
                    show: new ObservableValue<boolean | undefined>(undefined)
                };
                this.commitSections.push(commitsSection);
            }

            // Remove the first (rename/delete) commit entry in a follow-rename block since
            // it is the same commit shown in the last line of the previous block
            if (section.lastCommitIdBeforeRename.value === firstCommit.commitId) {
                commitResults.commits = commitResults.commits.slice(1);
            }
        }

        let newCommits: Array<ICommit | ObservableValue<ICommit | undefined>> = commitResults.commits.map((c, index) => {
            const changeType = this.props.isSingleFileHistory && c.changes && c.changes.length ? c.changes[0].changeType : undefined;
            const commit: ICommit = {
                changeType,
                comment: c.comment || "",
                commit: c,
                graphRow: commitResults.graphRows && commitResults.graphRows[index + (skip ? skip : 0)],
                linkProps: {
                    href: commitsDataService.getCommitUrl(
                        c.commitId,
                        this.props.version.versionType === GitVersionType.Branch ? branchNameToRef(this.props.version.version) : undefined,
                        this.props.isSingleFileHistory ? this.props.filePath : undefined,
                        this.props.isSingleFileHistory ? ((changeType! & VersionControlChangeType.Edit) !== 0 ? "compare" : "contents") : undefined
                    )
                },
                projectName: getTFSProject(this.context.pageContext)!.name,
                pullRequest: new ObservableValue(undefined),
                repositoryId: getGitRepository(this.context.pageContext)!.id,
                repositoryName: getGitRepository(this.context.pageContext)!.name,
                shortCommitId: getShortCommitId(c.commitId),
                statuses: new ObservableArray<GitStatus>([]),
                tags: new ObservableArray()
            };
            return commit;
        });

        if (skip) {
            newCommits = section.commits.value.concat(newCommits);
        }

        if (!noGraph && commitResults.graphRows) {
            const selectedId = this.historyGraph && this.historyGraph.selectedCommitId.value;

            this.historyGraph = new HistoryGraph(commitResults.graphRows);

            selectedId && this.historyGraph && this.historyGraph.select(selectedId);
        }

        section.commits.value = newCommits;

        commitsDataService
            .fetchCommitDetails(
                commitResults.commits.map(commit => commit.commitId),
                this.props.compareVersion ?? this.props.version
            )
            .then(fullCommitResults => {
                if (fullCommitResults) {
                    if (fullCommitResults.pullRequests) {
                        for (const commitId in fullCommitResults.pullRequests) {
                            const commit = section.commits.value.find((commit: ICommit) => commit.commit.commitId === commitId) as
                                | ICommit
                                | undefined;
                            if (commit && commit.pullRequest) {
                                commit.pullRequest.value = fullCommitResults.pullRequests[commitId];
                            }
                        }
                    }

                    for (const commitRef of fullCommitResults.commits) {
                        const tags = fullCommitResults.tags && fullCommitResults.tags[commitRef.commitId];
                        if (commitRef.statuses || tags) {
                            const commit = section.commits.value.find((commit: ICommit) => commit.commit.commitId === commitRef.commitId) as
                                | ICommit
                                | undefined;
                            if (commit && commit.statuses) {
                                if (commitRef.statuses) {
                                    commit.statuses.value = commitRef.statuses;
                                }
                                if (tags) {
                                    if (commit.tags) {
                                        commit.tags.push(...tags);
                                    } else {
                                        commit.tags = new ObservableArray<ICommitTagRef>(tags);
                                    }
                                }
                            }
                        }
                    }
                }

                this.areDetailsLoaded.value = true;
            });

        this.loadingMore = false;
    }

    private getLoadingRows(): ObservableArray<ICommit | ObservableValue<ICommit | undefined>> {
        // Use 3 loading rows when loading history
        return new ObservableArray([new ObservableValue(undefined), new ObservableValue(undefined), new ObservableValue(undefined)]);
    }
}

Components.add("repos-commits-history", CommitsHistoryView);
